import type { TranslatedString } from "./TranslatedString";
import type { GettextInterface } from "@indeed/indeed-gettext";

import { useI18n as useCoreI18n } from "./I18nContext";

import { useMemo } from "react";

type BrandedGettextInterface = Omit<
  GettextInterface,
  | "gettext"
  | "pgettext"
  | "ngettext"
  | "npgettext"
  | "formatlist"
  | "getListFormatPattern"
  | "split"
> & {
  /**
   * Translates a string to the user's locale.
   *
   * Uses `{0}`, `{1}`, etc. for token replacement.
   *
   * @param msgId - The string to translate (English source text)
   * @param args - Optional: array of args for `{0}`, `{1}`, etc., OR a single string for `{0}` only
   * @returns The translated string as a TranslatedString
   *
   * @example
   * ```tsx
   * const { gettext } = useBrandedI18n();
   *
   * // Simple translation (no args)
   * const title = gettext('Add Language');
   *
   * // Single argument - replaces {0} only
   * const hello = gettext('Hello {0}', 'world');
   * // Returns: "Hello world"
   *
   * // Multiple arguments - pass as array
   * const greeting = gettext('{0} and {1}', ['Alice', 'Bob']);
   * // Returns: "Alice and Bob"
   *
   * // Add translator comments with `// #` on the line IMMEDIATELY before msgId:
   * const label = gettext(
   *     // # Button label shown when user saves their profile changes
   *     'Save'
   * );
   * ```
   */
  gettext: (...args: Parameters<GettextInterface["gettext"]>) => TranslatedString;

  /**
   * Translates a string with a context hint for translators.
   *
   * Use when the same English word needs different translations based on context.
   * The `msgCtxt` and `msgId` together form the unique key for translation lookup.
   * Note: An empty string msgCtxt and an absent msgCtxt do NOT mean the same thing.
   *
   * **Important**: Translators rely on `// #` comments (not context) to understand
   * how text is used. Always add comments explaining the usage!
   *
   * @param msgCtxt - Context string that disambiguates translations (not shown to users). Must not be empty.
   * @param msgId - The string to translate
   * @param args - Optional: array of args for `{0}`, `{1}`, etc., OR a single string for `{0}` only
   * @returns The translated string as a TranslatedString
   *
   * @example
   * ```tsx
   * const { pgettext } = useBrandedI18n();
   *
   * // Same word "resume" with different meanings
   * const cvLabel = pgettext('document type', 'Resume');
   * const continueLabel = pgettext('action', 'Resume');
   *
   * // With token replacement and translator comment
   * const applicantsTab = pgettext(
   *     'CandidateTypeTabs',
   *     // # A button to show job applicants. {0} is the count of applicants.
   *     'Applicants ({0})',
   *     [String(applicantCount)]
   * );
   * ```
   */
  pgettext: (...args: Parameters<GettextInterface["pgettext"]>) => TranslatedString;

  /**
   * Translates singular/plural forms based on a count.
   *
   * Handles complex pluralization rules for different locales (some languages
   * have more than 2 plural forms).
   *
   * @param msgId - Singular form (English)
   * @param msgIdPlural - Plural form (English)
   * @param n - The number determining which plural form to use
   * @param args - Optional: array of args for `{0}`, `{1}`, etc., OR a single string for `{0}` only
   * @returns The appropriate translated string as a TranslatedString
   *
   * @example
   * ```tsx
   * const { ngettext } = useBrandedI18n();
   *
   * // Basic pluralization with token replacement
   * const listings = ['job1', 'job2'];
   * const label = ngettext(
   *     'You have {0} listing',
   *     'You have {0} listings',
   *     listings.length,
   *     [String(listings.length)]
   * );
   * // If listings.length === 1: "You have 1 listing"
   * // If listings.length === 2: "You have 2 listings"
   * ```
   */
  ngettext: (...args: Parameters<GettextInterface["ngettext"]>) => TranslatedString;

  /**
   * Translates singular/plural forms with a context hint.
   *
   * Combines pgettext (context disambiguation) and ngettext (pluralization).
   *
   * @param msgCtxt - Context string that disambiguates translations. Must not be empty.
   * @param msgId - Singular form (English)
   * @param msgIdPlural - Plural form (English)
   * @param n - The number determining which plural form to use
   * @param args - Optional: array of args for `{0}`, `{1}`, etc., OR a single string for `{0}` only
   * @returns The appropriate translated string as a TranslatedString
   *
   * @example
   * ```tsx
   * const { npgettext } = useBrandedI18n();
   *
   * const jobs = ['dev', 'design'];
   * const message = npgettext(
   *     'jobs count message',
   *     // # Greeting shown on dashboard. {0}=user name, {1}=job count
   *     'Hi, {0}! You have {1} open job',
   *     'Hi, {0}! You have {1} open jobs',
   *     jobs.length,
   *     ['Developer', String(jobs.length)]
   * );
   * // If jobs.length === 1: "Hi, Developer! You have 1 open job"
   * // If jobs.length > 1:   "Hi, Developer! You have 2 open jobs"
   * ```
   */
  npgettext: (...args: Parameters<GettextInterface["npgettext"]>) => TranslatedString;

  /**
   * Formats a variable-length list of items according to locale conventions.
   *
   * Requires a format object defining how to join list items. The format object
   * must include patterns for different list positions.
   *
   * @param format - Object with `listStart`, `listMiddle`, `listEnd`, and `twoItemList` patterns
   * @param items - Array of strings to format as a list
   * @returns Formatted list as a TranslatedString
   *
   * @example
   * ```tsx
   * const { formatlist } = useBrandedI18n();
   *
   * const format = {
   *     listStart: '{0}, {1}',      // First two items
   *     listMiddle: '{0}, {1}',     // Middle items
   *     listEnd: '{0}, and {1}',    // Last two items
   *     twoItemList: '{0} and {1}', // When only two items
   * };
   *
   * formatlist(format, ['milk', 'eggs', 'cheese']);
   * // Returns: "milk, eggs, and cheese"
   *
   * formatlist(format, ['Alice', 'Bob']);
   * // Returns: "Alice and Bob"
   * ```
   */
  formatlist: (...args: Parameters<GettextInterface["formatlist"]>) => TranslatedString;

  /**
   * Gets the locale-specific pattern for formatting a list of a given length.
   *
   * Useful when you need the pattern itself (e.g., for custom formatting logic).
   *
   * @param format - Object with `listStart`, `listMiddle`, `listEnd`, and `twoItemList` patterns
   * @param length - The number of items in the list
   * @returns The pattern string with numbered placeholders
   *
   * @example
   * ```tsx
   * const { getListFormatPattern } = useBrandedI18n();
   *
   * const format = {
   *     listStart: '{0}, {1}',
   *     listMiddle: '{0}, {1}',
   *     listEnd: '{0}, and {1}',
   *     twoItemList: '{0} and {1}',
   * };
   *
   * getListFormatPattern(format, 3);
   * // Returns: "{0}, {1}, and {2}"
   *
   * getListFormatPattern(format, 2);
   * // Returns: "{0} and {1}"
   * ```
   */
  getListFormatPattern: (
    ...args: Parameters<GettextInterface["getListFormatPattern"]>
  ) => TranslatedString;

  /**
   * Split variants return arrays split by argument placeholders.
   *
   * Useful for embedding React elements inside of translations, where you need
   * to wrap part of the translated text in a component.
   *
   * @example
   * ```tsx
   * // Example: split.gettext('Hello {0}', [<ProfileLink />])
   * // Returns: ['Hello ', <ProfileLink />]
   * ```
   */
  split: {
    /**
     * Translates a string and returns an array split by argument placeholders.
     *
     * This is the primary way to embed React components within translated text.
     * The result alternates between string segments and the provided arguments.
     *
     * @param msgId - The string to translate
     * @param args - Array of arguments (can include React elements), OR single value for `{0}`
     * @returns Array containing strings and the passed arguments in order
     *
     * @example
     * ```tsx
     * const { split } = useBrandedI18n();
     *
     * // Single React element - can pass directly or as array
     * const parts = split.gettext('View your profile: {0}', <ProfileLink />);
     * // Returns: ['View your profile: ', <ProfileLink />]
     *
     * // Multiple arguments - must pass as array
     * const result = split.gettext(
     *     'Contact {0} or visit {1}',
     *     [<EmailLink />, <WebsiteLink />]
     * );
     * // Returns: ['Contact ', <EmailLink />, ' or visit ', <WebsiteLink />]
     *
     * // Render in JSX:
     * return <Text>{parts}</Text>;
     * ```
     */
    gettext: (...args: Parameters<GettextInterface["split"]["gettext"]>) => TranslatedString[];

    /**
     * Translates a context-aware string and returns an array split by arguments.
     *
     * @param msgCtxt - Context string for translation disambiguation
     * @param msgId - The string to translate
     * @param args - Array of arguments (can include React elements), OR single value for `{0}`
     * @returns Array containing strings and the passed arguments in order
     *
     * @example
     * ```tsx
     * const { split } = useBrandedI18n();
     *
     * const parts = split.pgettext(
     *     'legal agreement',
     *     'By signing up, you agree to our {0}',
     *     <TermsLink />
     * );
     * // Returns: ['By signing up, you agree to our ', <TermsLink />]
     * ```
     */
    pgettext: (...args: Parameters<GettextInterface["split"]["pgettext"]>) => TranslatedString[];

    /**
     * Translates a pluralized string and returns an array split by arguments.
     *
     * @param msgId - Singular form to translate
     * @param msgIdPlural - Plural form to translate
     * @param n - The number determining which form to use
     * @param args - Array of arguments (can include React elements), OR single value for `{0}`
     * @returns Array containing strings and the passed arguments in order
     *
     * @example
     * ```tsx
     * const { split } = useBrandedI18n();
     *
     * const parts = split.ngettext(
     *     '{0} item in {1}',
     *     '{0} items in {1}',
     *     itemCount,
     *     [<Badge count={itemCount} />, <CartLink />]
     * );
     * // Returns: [<Badge />, ' item in ', <CartLink />] (for singular)
     * // Returns: [<Badge />, ' items in ', <CartLink />] (for plural)
     * ```
     */
    ngettext: (...args: Parameters<GettextInterface["split"]["ngettext"]>) => TranslatedString[];

    /**
     * Translates a context-aware pluralized string and returns an array split by arguments.
     *
     * @param msgCtxt - Context string for translation disambiguation
     * @param msgId - Singular form to translate
     * @param msgIdPlural - Plural form to translate
     * @param n - The number determining which form to use
     * @param args - Array of arguments (can include React elements), OR single value for `{0}`
     * @returns Array containing strings and the passed arguments in order
     *
     * @example
     * ```tsx
     * const { split } = useBrandedI18n();
     *
     * const parts = split.npgettext(
     *     'search results header',
     *     'Found {0} result matching {1}',
     *     'Found {0} results matching {1}',
     *     results.length,
     *     [<CountBadge value={results.length} />, <SearchTermHighlight term={query} />]
     * );
     * ```
     */
    npgettext: (...args: Parameters<GettextInterface["split"]["npgettext"]>) => TranslatedString[];
  };
};

export type { BrandedGettextInterface };

/**
 * Hook for internationalization with type-safe TranslatedString return values.
 *
 * Wraps the core `@indeed/indeed-gettext` functionality and ensures all translated
 * strings are branded with `TranslatedString` type for compile-time safety.
 *
 * ## Token Replacement
 * Use `{0}`, `{1}`, etc. for argument placeholders (not `%s` or `%d`).
 * - Single arg: pass directly as second param (replaces `{0}` only)
 * - Multiple args: pass as array
 *
 * ## Translator Comments
 * Add `// #` comments on the line **immediately before** the msgId to help translators.
 * Comments placed elsewhere won't be extracted to PO files!
 * ```tsx
 * gettext(
 *     // # Shown when user completes profile setup
 *     'Profile complete!'
 * );
 * ```
 *
 * @example
 * ```tsx
 * import { useBrandedI18n, type TranslatedString } from '../../shared/I18n';
 *
 * interface Props {
 *     title: TranslatedString; // Only accepts translated strings
 * }
 *
 * const MyComponent = ({ title }: Props) => {
 *     const { gettext, ngettext, pgettext, split } = useBrandedI18n();
 *
 *     // Single argument - pass directly
 *     const hello = gettext('Hello {0}', 'world');
 *
 *     // Multiple arguments - pass as array
 *     const greeting = gettext('{0} and {1}', ['Alice', 'Bob']);
 *
 *     // Pluralization with args array
 *     const items = ['a', 'b', 'c'];
 *     const itemText = ngettext(
 *         'You have {0} item',
 *         'You have {0} items',
 *         items.length,
 *         [String(items.length)]
 *     );
 *
 *     // Context-specific translation
 *     const resumeDoc = pgettext('document type', 'Resume');
 *     const resumeAction = pgettext('action', 'Resume');
 *
 *     // Embedding React elements in translations
 *     const linkParts = split.gettext('View {0}', <ProfileLink />);
 *     // Returns: ['View ', <ProfileLink />]
 *
 *     return (
 *         <View>
 *             <Text>{title}</Text>
 *             <Text>{hello}</Text>
 *             <Text>{greeting}</Text>
 *             <Text>{itemText}</Text>
 *             <Text>{linkParts}</Text>
 *         </View>
 *     );
 * };
 *
 * // Usage - TranslatedString enforces i18n compliance:
 * const App = () => {
 *     const { gettext } = useBrandedI18n();
 *
 *     // ✓ Valid - passing a TranslatedString
 *     return <MyComponent title={gettext('Profile Settings')} />;
 *
 *     // ✗ Invalid - cannot pass raw string
 *     // return <MyComponent title="Profile Settings" />; // Type error!
 * };
 * ```
 */
/**
 * Brands a core `GettextInterface` so every gettext-family call returns a
 * `TranslatedString`. Shared by {@link useBrandedI18n} and `useSafeI18n` (the
 * latter brands a safety-wrapped instance).
 */
export function brandGettext(coreI18n: GettextInterface): BrandedGettextInterface {
  return {
    ...coreI18n,
    gettext: (...args) => coreI18n.gettext(...args) as TranslatedString,
    pgettext: (...args) => coreI18n.pgettext(...args) as TranslatedString,
    ngettext: (...args) => coreI18n.ngettext(...args) as TranslatedString,
    npgettext: (...args) => coreI18n.npgettext(...args) as TranslatedString,
    formatlist: (...args) => coreI18n.formatlist(...args) as TranslatedString,
    getListFormatPattern: (...args) => coreI18n.getListFormatPattern(...args) as TranslatedString,
    split: {
      gettext: (...args) => coreI18n.split.gettext(...args) as TranslatedString[],
      pgettext: (...args) => coreI18n.split.pgettext(...args) as TranslatedString[],
      ngettext: (...args) => coreI18n.split.ngettext(...args) as TranslatedString[],
      npgettext: (...args) => coreI18n.split.npgettext(...args) as TranslatedString[],
    },
  };
}

export function useBrandedI18n(): BrandedGettextInterface {
  const coreI18n = useCoreI18n();

  return useMemo(() => brandGettext(coreI18n), [coreI18n]);
}
